import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../../components/Modal';
import { BodyLarge, ParagraphLargeText } from '../../../components/Texts';
import Button from '../../../components/Button';
import styled, { keyframes } from 'styled-components';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../../common/responsive';
import { formatUSDEnsure2Digits } from '../../../common/currency';

const floatAnimation = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-8px);
  }
`;

export const CardContainer = styled.div`
  position: relative;
  display: inline-block;
  border-radius: 15px;
  overflow: hidden;
  transition: transform 0.5s ease;
  animation-delay: ${(props) => props.delay || '0s'};
  animation: ${floatAnimation} 3s ease-in-out infinite;

  width: 182px;
  height: 312px;
  perspective: 1000px;
  cursor: pointer;

  @media (hover: hover) {
    &:hover {
      transform: scale(1.03);
      filter: drop-shadow(0 10px 20px rgba(0, 0, 0, 0.6));
      animation-play-state: paused;
    }
  }

  @media ${PHONE_MEDIA_QUERY} {
    width: 182px;
    border-radius: 6px;
    height: 312px;
  }
`;

export const Card = styled.div`
  position: relative;
  width: 182px;
  height: 312px;
  transition: transform 0.6s;
  transform-style: preserve-3d;

  ${({ flipped }) =>
    flipped &&
    `
    transform: rotateY(180deg);
  `}

  @media ${PHONE_MEDIA_QUERY} {
    width: 182px;
    height: 312px;
  }
`;

const ContentContainer = styled.div`
  width: 100%;
  overflow: hidden;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  justify-content: center;
  overflow: visible;
  @media ${PHONE_MEDIA_QUERY} {
    gap: 0px;
  }
`;

const StyledModal = styled(Modal)`
  .modal-content {
    overflow: hidden;
    max-height: 700px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
`;

const CardsContainer = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  position: relative;
  width: calc((182px * 3) + 30px);
  height: 100%;
  transition: transform 0.6s;
  @media ${PHONE_MEDIA_QUERY} {
    justify-content: space-around;
    width: calc(100vw - 32px);
  }
`;

const CardImage = styled.div`
  position: absolute;
  backface-visibility: hidden;
  display: flex;
  width: 182px;
  height: 312px;
  align-items: center;
  justify-content: center;
  background-image: ${(props) => (props.src ? `url(${props.src})` : 'none')};
  background-size: contain;
  background-repeat: no-repeat;
  @media ${PHONE_MEDIA_QUERY} {
    width: 182px;
    height: 312px;
  }
`;

const CardFrontContainer = styled.div`
  position: absolute;
  width: 182px;
  height: 312px;
  display: flex;
  align-items: flex-end;
  font-size: 17px;
  font-weight: 600;
  color: ${({ theme }) => theme.text.primary};
  justify-content: flex-start;
  padding-left: 7%;
  padding-bottom: 4%;
`;

const NumbersContainer = styled.p`
  margin-top: 8px;
`;

const CardRevealModal = ({ onClose, tokens }) => {
  const { t } = useTranslation();
  const [flippedCards, setFlippedCards] = useState([false, false, false]);

  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);

  const [currentCardIndex, setCurrentCardIndex] = useState(0);

  const handleCardClick = (index) => {
    if (!flippedCards[index]) {
      const newFlippedCards = [...flippedCards];
      newFlippedCards[index] = true;
      setFlippedCards(newFlippedCards);
    }
  };

  return (
    <StyledModal
      onClose={() => {
        onClose();
      }}
    >
      <ContentContainer>
        <ParagraphLargeText>{t('cardRevealModal.title')}</ParagraphLargeText>
        <BodyLarge>{t('cardRevealModal.clickToReveal')}</BodyLarge>
        <CardsContainer>
          {isMobile ? (
            <CardContainer onClick={() => handleCardClick(currentCardIndex)}>
              <Card
                style={
                  !flippedCards[currentCardIndex]
                    ? { transition: 'transform 0s ease' }
                    : {}
                }
                flipped={flippedCards[currentCardIndex]}
              >
                <CardImage
                  style={{ transform: 'rotateY(180deg)' }}
                  src={`/packs/${tokens[currentCardIndex]?.baseToken}.webp`}
                >
                  <CardFrontContainer>
                    {formatUSDEnsure2Digits(
                      tokens[currentCardIndex]?.usdAmount,
                    )}{' '}
                    USD
                  </CardFrontContainer>
                </CardImage>
                <CardImage src={'/packs/back.webp'} />
              </Card>
            </CardContainer>
          ) : (
            <>
              {tokens.map((token, index) => (
                <CardContainer
                  key={index}
                  onClick={() => handleCardClick(index)}
                >
                  <Card flipped={flippedCards[index]}>
                    <CardImage
                      style={{ transform: 'rotateY(180deg)' }}
                      src={`/packs/${token.baseToken}.webp`}
                    >
                      <CardFrontContainer>
                        {formatUSDEnsure2Digits(token.usdAmount)} USD
                      </CardFrontContainer>
                    </CardImage>
                    <CardImage src={'/packs/back.webp'} />
                  </Card>
                </CardContainer>
              ))}
            </>
          )}
        </CardsContainer>
        {isMobile && (
          <NumbersContainer>
            {currentCardIndex + 1} / {tokens.length}
          </NumbersContainer>
        )}
        <ButtonContainer>
          <Button
            disabled={
              isMobile
                ? !flippedCards[currentCardIndex]
                : !flippedCards.every((card) => card)
            }
            onClick={() => {
              if (isMobile) {
                if (currentCardIndex === tokens.length - 1) {
                  onClose();
                } else {
                  setCurrentCardIndex(currentCardIndex + 1);
                }
              } else {
                onClose();
              }
            }}
          >
            CONTINUE
          </Button>
        </ButtonContainer>
      </ContentContainer>
    </StyledModal>
  );
};

export default CardRevealModal;
